import type { GameInstanceConfig, GamePlugin, RoundContext } from '../types';
import { computeDotsTotals, DEFAULT_DOTS } from '@/lib/games/formats/dots';
import { computeSixesTotals } from '@/lib/games/formats/sixes';
import { computeWolfTotals, mergeWolfConfig } from '@/lib/games/formats/wolf';
import { balanceNetOwed } from '@/lib/games/formats/balance';
import { ryderCupTeamPoints, scrambleTeamTotals, vegasHoleDiff } from '@/lib/games/formats/team-formats';
import {
  applyMoneyToNetOwed,
  getHandicapAllowance,
  getMoneyAmount,
  getStakeConfig,
  pointsFromTotals,
} from '../stakes-helpers';
import { isPerHoleStake } from '../stake-settlement';
import { dotsHolePoints } from '@/lib/games/formats/dots';
import type { DotsHoleState, WolfHoleState } from '../types';

function holeStateMap(config: GameInstanceConfig): Record<number, WolfHoleState | DotsHoleState> {
  return (config.holeState || {}) as Record<number, WolfHoleState | DotsHoleState>;
}

export const dotsPlugin: GamePlugin = {
  id: 'dots',
  label: 'Dots / Junk',
  description: 'Birdies, sandies, greenies and more',
  requiresTeams: false,
  minPlayers: 4,
  defaultStakes: { scoringType: 'both', money: 1 },
  compute(ctx: RoundContext, config: GameInstanceConfig) {
    const stake = getStakeConfig(ctx.stakes, 'dots');
    const keys = ctx.players.map((p) => p.playerKey);
    const enabled = config.dotsEnabled || DEFAULT_DOTS.map((d) => d.id);
    const dots = DEFAULT_DOTS.filter((d) => enabled.includes(d.id));
    const totals = computeDotsTotals(keys, ctx.scores, ctx.holes, dots, holeStateMap(config) as Record<number, DotsHoleState>);
    const pointVals = keys.map((k) => totals[k] || 0);
    let netOwedRaw: Record<string, number>;
    if (isPerHoleStake(stake)) {
      const rate = getMoneyAmount(stake);
      netOwedRaw = {};
      keys.forEach((k) => { netOwedRaw[k] = 0; });
      ctx.holes.forEach((h, hi) => {
        const hs = holeStateMap(config)[hi + 1] ?? holeStateMap(config)[hi];
        const holePts = keys.map((key) => {
          const g = ctx.scores[key]?.[hi];
          if (g == null) return 0;
          return dotsHolePoints(hi, key, g, h, dots, hs as DotsHoleState);
        });
        const holeNet = balanceNetOwed(keys, holePts, rate);
        keys.forEach((k, idx) => { netOwedRaw[k] += holeNet[k] || 0; });
      });
    } else {
      netOwedRaw = balanceNetOwed(keys, pointVals, getMoneyAmount(stake));
    }
    const netOwed = applyMoneyToNetOwed(netOwedRaw, stake);
    const ranked = keys.map((k) => ({ k, v: totals[k] || 0 })).sort((a, b) => b.v - a.v);
    return {
      formatId: 'dots',
      label: 'Dots / Junk',
      summary: ranked[0] ? `${ctx.players.find((p) => p.playerKey === ranked[0].k)?.name} leads` : 'Dots',
      standings: ranked.map((r, i) => ({
        playerKey: r.k,
        name: ctx.players.find((p) => p.playerKey === r.k)?.name || r.k,
        value: `${r.v} pts`,
        rank: i + 1,
      })),
      netOwed,
      pointsAwarded: pointsFromTotals(keys, totals, stake),
      winners: ranked[0] ? [ranked[0].k] : [],
    };
  },
};

export const wolfPlugin: GamePlugin = {
  id: 'wolf',
  label: 'Wolf',
  description: 'Rotating wolf each hole',
  requiresTeams: false,
  minPlayers: 4,
  defaultStakes: { scoringType: 'both', money: 2 },
  compute(ctx: RoundContext, config: GameInstanceConfig) {
    const stake = getStakeConfig(ctx.stakes, 'wolf');
    const wolfCfg = mergeWolfConfig(config.wolfConfig);
    const { totals, log } = computeWolfTotals(
      ctx.players,
      ctx.scores,
      ctx.holes,
      holeStateMap(config) as Record<number, WolfHoleState>,
      wolfCfg,
      getHandicapAllowance(stake, 'wolf'),
    );
    const keys = ctx.players.map((p) => p.playerKey);
    const pointVals = keys.map((k) => totals[k] || 0);
    const dollarsPerPoint = stake.moneyPerPoint ?? getMoneyAmount(stake);
    const netOwed = applyMoneyToNetOwed(balanceNetOwed(keys, pointVals, dollarsPerPoint), stake);
    const ranked = keys.map((k) => ({ k, v: totals[k] || 0 })).sort((a, b) => b.v - a.v);
    return {
      formatId: 'wolf',
      label: config.wolfMode === 'lone-only' ? 'Lone Wolf' : 'Wolf',
      summary: ranked[0] ? `${ctx.players.find((p) => p.playerKey === ranked[0].k)?.name} ${ranked[0].v} pts` : 'Wolf',
      standings: ranked.map((r, i) => ({
        playerKey: r.k,
        name: ctx.players.find((p) => p.playerKey === r.k)?.name || r.k,
        value: `${r.v} pts`,
        rank: i + 1,
      })),
      netOwed,
      pointsAwarded: pointsFromTotals(keys, totals, stake),
      detail: { log, wolfConfig: wolfCfg },
      winners: ranked[0] ? [ranked[0].k] : [],
    };
  },
};

export const sixesPlugin: GamePlugin = {
  id: 'sixes',
  label: 'Sixes',
  description: 'Rotating partners every 6 holes',
  requiresTeams: false,
  minPlayers: 4,
  defaultStakes: { scoringType: 'money', money: 2 },
  compute(ctx: RoundContext, config: GameInstanceConfig) {
    const stake = getStakeConfig(ctx.stakes, 'sixes');
    const keys = config.playerKeys?.length === 4
      ? config.playerKeys
      : ctx.players.map((p) => p.playerKey);
    const totals = computeSixesTotals(ctx.players, ctx.scores, ctx.holes, keys);
    const pointVals = keys.map((k) => totals[k] || 0);
    const netOwed = applyMoneyToNetOwed(balanceNetOwed(keys, pointVals, getMoneyAmount(stake)), stake);
    return {
      formatId: 'sixes',
      label: 'Sixes',
      summary: 'Partner rotation 6-6-6',
      standings: keys.map((k, i) => ({
        playerKey: k,
        name: ctx.players.find((p) => p.playerKey === k)?.name || k,
        value: `${totals[k] || 0} pts`,
        rank: i + 1,
      })).sort((a, b) => Number(b.value) - Number(a.value)),
      netOwed,
      pointsAwarded: pointsFromTotals(keys, totals, stake),
    };
  },
};

function teamScorePlugin(id: string, label: string, mode: 'scramble' | 'shamble'): GamePlugin {
  return {
    id,
    label,
    description: mode === 'scramble' ? 'Team plays best shot' : 'Best drive, own ball in',
    requiresTeams: true,
    minPlayers: 2,
    defaultStakes: { scoringType: 'money', money: 10 },
    compute(ctx: RoundContext, config: GameInstanceConfig) {
      const stake = getStakeConfig(ctx.stakes, id);
      const teams = ctx.teams;
      if (!teams || teams.length < 2) {
        return { formatId: id, label, summary: 'Set up teams', netOwed: {} };
      }
      const teamScores = config.teamScores || { '0': [], '1': [] };
      const { team0, team1 } = scrambleTeamTotals(teams, teamScores as Record<string, (number | null)[]>, ctx.holes);
      const diff = team1 - team0;
      const bet = getMoneyAmount(stake) * 100;
      const netOwed: Record<string, number> = {};
      ctx.players.forEach((p) => { netOwed[p.playerKey] = 0; });
      if (diff !== 0) {
        const win = diff > 0 ? 1 : 0;
        const lose = diff > 0 ? 0 : 1;
        const owe = Math.abs(diff) * bet / 9;
        teams[win].forEach((k) => { netOwed[k] = (netOwed[k] || 0) + owe / teams[win].length; });
        teams[lose].forEach((k) => { netOwed[k] = (netOwed[k] || 0) - owe / teams[lose].length; });
      }
      const summary = diff === 0 ? 'All square' : diff > 0 ? 'Team B leads' : 'Team A leads';
      return {
        formatId: id,
        label,
        summary,
        netOwed: applyMoneyToNetOwed(netOwed, stake),
        detail: { team0, team1, mode },
      };
    },
  };
}

export const scramblePlugin = teamScorePlugin('scramble', 'Scramble', 'scramble');
export const shamblePlugin = teamScorePlugin('shamble', 'Shamble', 'shamble');

export const ryderCupPlugin: GamePlugin = {
  id: 'ryder-cup',
  label: 'Ryder Cup',
  description: 'Team match play series',
  requiresTeams: true,
  minPlayers: 4,
  defaultStakes: { scoringType: 'points' },
  compute(ctx: RoundContext, _config: GameInstanceConfig) {
    const stake = getStakeConfig(ctx.stakes, 'ryder-cup');
    const teams = ctx.teams;
    if (!teams || teams.length < 2) {
      return { formatId: 'ryder-cup', label: 'Ryder Cup', summary: 'Set up teams', netOwed: {} };
    }
    const { teamAPoints, teamBPoints } = ryderCupTeamPoints(teams, ctx.players, ctx.holes, ctx.scores);
    const bet = getMoneyAmount(stake) * 100;
    const netOwed: Record<string, number> = {};
    ctx.players.forEach((p) => { netOwed[p.playerKey] = 0; });
    if (teamAPoints !== teamBPoints) {
      const win = teamAPoints > teamBPoints ? 0 : 1;
      const lose = win === 0 ? 1 : 0;
      teams[win].forEach((k) => { netOwed[k] = bet / teams[win].length; });
      teams[lose].forEach((k) => { netOwed[k] = -bet / teams[lose].length; });
    }
    return {
      formatId: 'ryder-cup',
      label: 'Ryder Cup',
      summary: `Team A ${teamAPoints} – ${teamBPoints} Team B`,
      netOwed: applyMoneyToNetOwed(netOwed, stake),
      detail: { teamAPoints, teamBPoints },
    };
  },
};

export const vegasPlugin: GamePlugin = {
  id: 'vegas',
  label: 'Vegas',
  description: '2v2 team scores form a number',
  requiresTeams: true,
  minPlayers: 4,
  defaultStakes: { scoringType: 'money', money: 5 },
  compute(ctx: RoundContext, _config: GameInstanceConfig) {
    const stake = getStakeConfig(ctx.stakes, 'vegas');
    const teams = ctx.teams;
    if (!teams || teams.length < 2) {
      return { formatId: 'vegas', label: 'Vegas', summary: 'Set up teams', netOwed: {} };
    }
    let teamADiff = 0;
    ctx.holes.forEach((_, hi) => {
      const d = vegasHoleDiff(teams, hi, ctx.scores);
      if (d != null) teamADiff += d;
    });
    const bet = getMoneyAmount(stake);
    const netOwed: Record<string, number> = {};
    ctx.players.forEach((p) => { netOwed[p.playerKey] = 0; });
    if (teamADiff !== 0) {
      const win = teamADiff > 0 ? 0 : 1;
      const lose = win === 0 ? 1 : 0;
      const owe = Math.abs(teamADiff) * bet * 100;
      teams[win].forEach((k) => { netOwed[k] = owe / teams[win].length; });
      teams[lose].forEach((k) => { netOwed[k] = -owe / teams[lose].length; });
    }
    return {
      formatId: 'vegas',
      label: 'Vegas',
      summary: teamADiff === 0 ? 'All square' : `Team ${teamADiff > 0 ? 'A' : 'B'} up`,
      netOwed: applyMoneyToNetOwed(netOwed, stake),
    };
  },
};
